<template>
  <div class="col-3 px-3 py-2">
    <h3 class="text-lg text-primary">
      {{ t('top_product') }}
    </h3>

    <div
      v-if="mainProduct"
      class="flex text-center flex-col relative"
    >
      <div class="embed-responsive mb-0 flex-grow-1">
        <RemoteResponsiveImage
          :product-image-data="mainProduct.images[0]?.data"
          img-class="embed-responsive-item relative image-cover"
          :alt="mainProduct.name"
          sizes="10vw"
        />
      </div>
      <router-link
        v-close-popper
        class="mb-5 stretched-link zindex-1"
        :to="{ name: 'productPath', params: { permalink: mainProduct.permalink } }"
      >
        <h4 class="text-primary mt-4 mb-2">
          {{ mainProduct.name }}
        </h4>
        <strong class="h2 text-primary font-bold">
          <template v-if="mainProduct.quantityDiscounts">
            {{
              discountPriceFormatter(
                showFromLabel(mainProduct.quantityDiscounts, mainProduct.plusClubPrice) ? t('from') : '',
                mainProduct.plusClubPrice,
                getCurrentCustomer?.inPlusClub,
                mainProduct.quantityDiscounts[0].price,
                mainProduct.quantityDiscounts[mainProduct.quantityDiscounts.length - 1].price,
                mainProduct.currency,
              )
            }}
          </template>
          <template v-else>{{ numberToCurrency(mainProduct.price, mainProduct.currency) }}</template>
        </strong>
      </router-link>

      <div class="zindex-2">
        <AddToCartButton
          v-if="!mainProduct.isIngredient && mainProduct.state === 'available'"
          :product="mainProduct"
          gtm-source="top-search"
        />
        <router-link
          v-else
          v-close-popper
          :to="{ name: 'productPath', params: { permalink: mainProduct.permalink } }"
          type="button"
          class="btn btn-warning p-3 sm:p-2 stretched-link rounded-circle"
          data-toggle="product-detail-modal"
          aria-label="product info"
        >
          <i class="mi-info icon-20" />
        </router-link>
      </div>
    </div>
  </div>
  <div
    v-if="products.length > 0"
    class="col-6 pr-0 border-left border-primary-20 pl-3 py-2 pr-1"
  >
    <h3 class="text-lg text-primary">
      {{ t('other_products') }}
    </h3>
    <div class="search-results">
      <ul class="list-unstyled m-0 row search-results-desktop">
        <li
          v-for="(product, index) in products"
          :key="product.id"
          class="flex py-1 px-1 pr-3 col-6"
          :class="{ 'mt-4': index > 1 }"
        >
          <div class="row flex-grow-1 items-start">
            <div class="col-4 px-1">
              <router-link
                v-close-popper
                :to="{ name: 'productPath', params: { permalink: product.permalink } }"
              >
                <div
                  v-if="product.images.length > 0"
                  class="embed-responsive mb-0 flex-grow-1"
                >
                  <RemoteResponsiveImage
                    :product-image-data="product.images[0]?.data"
                    img-class="embed-responsive-item relative image-cover"
                    :alt="product.name"
                    sizes="10vw"
                  />
                </div>
              </router-link>
            </div>
            <div class="col-8 px-1 relative">
              <router-link
                v-close-popper
                class="block p-1 text-sm flex-grow-1 leading-sm stretched-link relative zindex-1"
                :to="{ name: 'productPath', params: { permalink: product.permalink } }"
              >
                <span>{{ product.name }}</span>
                <strong class="text-md">
                  <template v-if="product.quantityDiscounts">
                    {{
                      discountPriceFormatter(
                        showFromLabel(product.quantityDiscounts, product.plusClubPrice) ? t('from') : '',
                        product.plusClubPrice,
                        getCurrentCustomer?.inPlusClub,
                        product.quantityDiscounts[0].price,
                        product.quantityDiscounts[product.quantityDiscounts.length - 1].price,
                        product.currency,
                      )
                    }}
                  </template>
                  <template v-else>{{ numberToCurrency(product.price, product.currency) }}</template>
                </strong>
              </router-link>
              <AddToCartButton
                v-if="!product.isIngredient && product.state === 'available'"
                class="w-auto float-right relative zindex-2"
                :product="product"
                :icon-button-only="true"
                gtm-source="navbar-products-search"
              />
              <router-link
                v-else
                v-close-popper
                :to="{ name: 'productPath', params: { permalink: product.permalink } }"
                type="button"
                class="btn btn-warning p-3 sm:p-2 stretched-link rounded-circle float-right relative zindex-2"
                data-toggle="product-detail-modal"
                aria-label="product info"
              >
                <i class="mi-info icon-20" />
              </router-link>
            </div>
          </div>
        </li>
      </ul>
      <div
        v-if="showAllResults"
        class="text-center mt-5"
      >
        <button
          class="btn  btn-primary px-5 md:px-3 xl:px-5 font-medium rounded-pill"
          type="button"
          @click="goToSearchView"
        >
          {{ t('show_all_results') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import AddToCartButton from '@/components/buttons/ButtonAddToCartButton.vue'
import RemoteResponsiveImage from '@/components/general/BaseRemoteResponsiveImage.vue'
import { discountPriceFormatter } from '@/mixins/discountPriceFormatter'
import { numberToCurrency } from '@/mixins/numberToCurrency'
import { useAppStore } from '@/stores/AppStore'

const appStore = useAppStore()
const { getCurrentCustomer } = storeToRefs(appStore)

const props = defineProps({
  mainProduct: {
    type: Object,
    required: true,
  },
  products: {
    type: Array,
    required: true,
  },
  searchText: {
    type: String,
    default: '',
  },
  showAllResults: {
    type: Boolean,
    default: true,
  },
})

const { t } = useI18n()
const router = useRouter()

const goToSearchView = () => {
  const url = router.resolve({ name: 'searchPath', params: { search: props.searchText } })
  window.location.href = url.href
}

// Defines if "From [some price]" label should be visible.
const showFromLabel = (quantityDiscounts, plusClubPrice) => {
  return quantityDiscounts && (!plusClubPrice || !getCurrentCustomer.value?.inPlusClub)
}
</script>

<i18n>
  {
    "cs": {
    "from": "Od",
    "other_products": "Další dobroty",
    "show_all_results": "Zobrazit všechny výsledky",
    "top_product": "Top Vyhledávání"
},
    "sk": {
    "from": "Od",
    "other_products": "Ďalšie dobroty",
    "show_all_results": "Zobraziť všetky výsledky",
    "top_product": "Top produkt"
},
    "pl": {
    "from": "Od",
    "other_products": "Inne smakołyki",
    "show_all_results": "Zobacz wszystkie wyniki",
    "top_product": "Super produkt"
},
    "hu": {
    "from": "Ft-tól",
    "other_products": "Egyéb finomságok",
    "show_all_results": "Összes eredmény megtekintése",
    "top_product": "Kedvenc termék"
},
    "de": {
    "from": "Von",
    "other_products": "Mehr Leckereien",
    "show_all_results": "Alle Ergebnisse anzeigen",
    "top_product": "Top-Suche"
},
    "at": {
    "from": "Von",
    "other_products": "Mehr Leckereien",
    "show_all_results": "Alle Ergebnisse anzeigen",
    "top_product": "Top-Suche"
},
    "ro": {
    "from": "De la",
    "other_products": "Alte bunătăți",
    "show_all_results": "Vezi toate rezultatele",
    "top_product": "Produs grozav"
}
  }
</i18n>
